import React, { useEffect, useState } from "react";
import styledComponents from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserLoginMutation, useGetCoopQuery } from "../store/auth/authApi";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCompanyId } from "../store/auth/index.js";
import {
  setCoopData,
  setToken,
  setCompanyId,
  setUserType,
  setEnums,
  setUserData,
  setSubscriptionStatus,
} from "../store/auth/index.js";
import { toast } from "react-hot-toast";
import LoginPage from "../components/Modules/Login";
import { ModalCompAdvance } from "../components/Ui/ModalComp";
import { ButtonComp } from "../components";
import { useGetEnumsQuery } from "../store/generalSelections/generalSelection";
import LogRocket from "logrocket";
import { normalizePhoneNumber } from "../utils/phoneUtils";
import { persistThemeFromCoopPayload } from "../theme/companyTheme";
import { useInvestorLoginMutation } from "../store/investor/investorAuthApi";
import { getInvestorQueryParams } from "../utils/investorRouteHelpers";
import { resolveUnifiedPostLoginPath } from "../utils/postAuthRedirect";
import { clearLegacyInvestorMockStorage } from "../utils/investorMockStorageCleanup";
import { normalizeStaffProfilePayload } from "../utils/staffRoleModules";

// import { storage } from "../utils/storage";

export const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preservedQuery = getInvestorQueryParams(searchParams);
  const [fullhost, setFullhost] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [loginErrorKind, setLoginErrorKind] = useState("generic");
  const coopIdFromRedux = useSelector(selectCurrentCompanyId);

  const showLoginError = (message, kind = "generic") => {
    setLoginErrorKind(kind);
    setLoginError(
      message || "Login failed. Please check your credentials and try again."
    );
  };

  /** Wrong email/password — no need for a second investor login round-trip. */
  const isCredentialAuthFailure = (message) => {
    const text = String(message || "").toLowerCase();
    return (
      text.includes("invalid login") ||
      text.includes("invalid credential") ||
      text.includes("incorrect password") ||
      text.includes("wrong password") ||
      text.includes("authentication failed") ||
      text.includes("unauthorized")
    );
  };

  /** Membership / account blocks — show immediately; do not try investor login. */
  const getTerminalAuthFailureKind = (message) => {
    const text = String(message || "").toLowerCase();
    if (
      text.includes("membership has ended") ||
      text.includes("exited") ||
      text.includes("can no longer sign in") ||
      text.includes("can no longer access this cooperative")
    ) {
      return "exited";
    }
    if (text.includes("suspended")) return "suspended";
    if (
      text.includes("yet to be approved") ||
      text.includes("not approved") ||
      text.includes("awaiting approval")
    ) {
      return "pending";
    }
    return null;
  };

  const extractLoginErrorMessage = (error) => {
    return (
      error?.data?.message ||
      error?.error?.data?.message ||
      error?.message ||
      (typeof error?.data === "string" ? error.data : null) ||
      "Login failed"
    );
  };

  useEffect(() => {
    setFullhost(window.location.host);
  }, []);

  const { data, isLoading, isSuccess } = useGetCoopQuery(
    { domain_url: fullhost },
    { skip: !fullhost },
  );
  const companyDetails = data?.data;
  const investorPortalEnabled = Boolean(
    companyDetails?.investor_capabilities?.investor_portal_enabled ||
      companyDetails?.investor_capabilities?.investment_enabled ||
      companyDetails?.investor_capabilities?.marketplace_enabled ||
      companyDetails?.investor_capabilities?.real_estate_enabled ||
      companyDetails?.investor_capabilities?.cooperative_request_enabled
  );

  useEffect(() => {
    if (isSuccess && companyDetails) {
      dispatch(setCoopData(companyDetails));
      dispatch(setCompanyId(companyDetails?.company_id));
      persistThemeFromCoopPayload(companyDetails);
    }
  }, [isSuccess, companyDetails, dispatch]);

  const [login, { isLoading: isLogging }] = useUserLoginMutation();
  const [investorLogin] = useInvestorLoginMutation();

  async function handleLoginSubmit(values) {
    // Ensure company_id is always included from companyDetails, Redux state, or form values
    const companyId = companyDetails?.company_id || coopIdFromRedux || values.company_id;
    if (!companyId) {
      showLoginError("Company information not loaded. Please wait a moment and try again.");
      return;
    }

    // Normalize phone number if identifier is not an email
    let identifier = values.identifier;
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
    
    if (!isEmail) {
      // Get default country code from company or use Nigeria (234) as default
      const defaultCountryCode = companyDetails?.country?.code || '234';
      const normalized = normalizePhoneNumber(identifier, defaultCountryCode);
      
      if (!normalized || !normalized.startsWith('+')) {
        showLoginError("Please enter a valid phone number or email.");
        return;
      }
      
      identifier = normalized;
    }

    try {
      const loginResponse = await login({
        ...values,
        identifier: identifier,
        company_id: companyId,
      });

      if (loginResponse.error) {
        const apiError = extractLoginErrorMessage(loginResponse.error);
        const terminalKind = getTerminalAuthFailureKind(apiError);

        // Exited / suspended / pending — show clear message, never fall through to investor login.
        if (terminalKind) {
          showLoginError(apiError, terminalKind);
          return;
        }

        // Show credential failures immediately — skipping investor fallback avoids ~1–2s delay.
        if (!investorPortalEnabled || isCredentialAuthFailure(apiError)) {
          showLoginError(apiError, "generic");
          return;
        }

        // Only attempt investor login when hub is enabled and staff/member failure
        // might mean "this account is an investor" (e.g. user not found as staff).
        try {
          const investorResponse = await investorLogin({
            company_id: companyId,
            identifier: values.identifier,
            password: values.password,
          }).unwrap();
          if (investorResponse?.access_token && investorResponse?.profile) {
            clearLegacyInvestorMockStorage();
            dispatch(setToken(investorResponse.access_token));
            dispatch(setUserType(investorResponse.user_type || "investor"));
            dispatch(setUserData(investorResponse.profile));
            if (investorResponse.subscription) {
              dispatch(setSubscriptionStatus(investorResponse.subscription));
            }
            toast.success("Welcome back!");
            navigate(
              resolveUnifiedPostLoginPath({
                userType: investorResponse.user_type || "investor",
                queryParams: preservedQuery,
              }),
              { replace: true }
            );
            return;
          }
        } catch (investorError) {
          const investorMessage =
            extractLoginErrorMessage(investorError) ||
            apiError ||
            "Login failed. Please check your credentials.";
          // Prefer the original staff/member error when investor hub rejects the tenant,
          // so users are not told about investor portal when they were just signing in.
          const isPortalGateMessage =
            typeof investorMessage === "string" &&
            investorMessage.toLowerCase().includes("investor portal is not enabled");
          const fallback = isPortalGateMessage ? apiError : investorMessage;
          showLoginError(
            fallback,
            getTerminalAuthFailureKind(fallback) || "generic"
          );
          return;
        }
        showLoginError(apiError, "generic");
        return;
      }

      const { data, message, status } = loginResponse.data || {};
      
      // Check if this is a subscription-related error
      const isSubscriptionError = 
        message?.toLowerCase().includes("subscription") ||
        message?.toLowerCase().includes("expired") ||
        status === false;

      // If we have valid auth data (token and profile), proceed with login even if status is false
      // This allows login when subscription is expired (backend should return token regardless)
      if (data?.access_token && data?.profile) {
        clearLegacyInvestorMockStorage();
        let userProfile = data.profile;
        let userAppId = userProfile?.staff_id || userProfile?.member_id;
        let userAppName = userProfile?.name;
        let userAppEmail = userProfile?.email;
        let userAppActiveBranch = userProfile?.active_branch_id;
        let companyDomain = userProfile?.company?.domain_url;
        let companyName = userProfile?.company?.name;
        let userAppRole = userProfile?.role?.name;

        let logRocketObject = {
          name: userAppName,
          email: userAppEmail,
          activeBranch: userAppActiveBranch,
          companyDomain: companyDomain,
          companyName: companyName,
          userAppRole: userAppRole,
        };

        dispatch(setToken(data.access_token));
        dispatch(setUserType(data.user_type));
        dispatch(setUserData(normalizeStaffProfilePayload(data.profile)));
        if (data.profile?.company) {
          persistThemeFromCoopPayload(data.profile.company);
        }
        
        // Store subscription status if available
        if (data.subscription) {
          dispatch(setSubscriptionStatus(data.subscription));
        } else if (isSubscriptionError) {
          // If subscription error but no subscription data, create a default inactive status
          dispatch(setSubscriptionStatus({
            is_active: false,
            expires_at: null,
          }));
        }
        
        LogRocket.identify(userAppId, logRocketObject);
        
        // Show warning for subscription issues, success otherwise
        if (isSubscriptionError) {
          toast(message || "Subscription expired. Please contact your organization's administrator.", {
            icon: "⚠️",
            duration: 5000,
          });
        } else {
          toast.success(message || "Login successful");
        }

        navigate(
          resolveUnifiedPostLoginPath({
            userType: data.user_type,
            queryParams: preservedQuery,
            staffProfile: normalizeStaffProfilePayload(data.profile),
          }),
          { replace: true }
        );
      } else {
        const failMsg =
          message || "Login failed. Please check your credentials.";
        showLoginError(failMsg, getTerminalAuthFailureKind(failMsg) || "generic");
      }
    } catch (error) {
      console.error("Login error:", error);
      const failMsg = extractLoginErrorMessage(error);
      showLoginError(
        failMsg === "Login failed"
          ? "An error occurred during login. Please try again."
          : failMsg,
        getTerminalAuthFailureKind(failMsg) || "generic"
      );
    }
  }

  const { data: enumData, isSuccess: enumFetched } = useGetEnumsQuery();

  useEffect(() => {
    if (!enumFetched || !enumData?.data) return;

    const enumDataObject = {};
    enumData.data.forEach((item) => {
      enumDataObject[item.enum] = item.data.map((entry) => ({
        label: entry.name,
        value: entry.value,
      }));
    });
    dispatch(setEnums(enumDataObject));
  }, [enumFetched, enumData, dispatch]);

  return (
    <>
        <>
          <Style>
            <LoginPage
              handleLoginSubmit={handleLoginSubmit}
              companyDetails={companyDetails}
              isLoading={isLogging}
              isCompanyLoading={isLoading}
              preservedQuery={preservedQuery}
            />
          </Style>
          <ModalCompAdvance
            show={Boolean(loginError)}
            handleClose={() => {
              setLoginError(null);
              setLoginErrorKind("generic");
            }}
            close
            footerClose={false}
            centered
            size="sm"
            arrayComp={[
              {
                component: (
                  <div className="px-1 pt-2 pb-1 text-center">
                    <div
                      className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        backgroundColor:
                          loginErrorKind === "exited"
                            ? "rgba(49, 119, 140, 0.12)"
                            : "rgba(255, 67, 67, 0.12)",
                        color:
                          loginErrorKind === "exited" ? "#31778c" : "#ff4343",
                        fontSize: 26,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                      aria-hidden
                    >
                      !
                    </div>
                    <h5
                      className="fw-bold mb-2"
                      style={{ color: "#383838", fontSize: 20 }}
                    >
                      {loginErrorKind === "exited"
                        ? "Membership ended"
                        : loginErrorKind === "suspended"
                          ? "Account suspended"
                          : loginErrorKind === "pending"
                            ? "Awaiting approval"
                            : "Login unsuccessful"}
                    </h5>
                    <p
                      className="mb-1 mx-auto"
                      style={{
                        color: "#525252",
                        fontSize: 15,
                        lineHeight: 1.5,
                        maxWidth: 280,
                      }}
                    >
                      {loginError}
                    </p>
                    <p
                      className="mb-4 mx-auto"
                      style={{ color: "#a1a0a0", fontSize: 13, maxWidth: 280 }}
                    >
                      {loginErrorKind === "exited"
                        ? "Contact your cooperative administrator if you believe this is a mistake."
                        : loginErrorKind === "suspended" ||
                            loginErrorKind === "pending"
                          ? "Please contact your cooperative administrator for help."
                          : "Check your email/phone and password, then try again."}
                    </p>
                    <ButtonComp
                      type="button"
                      btnText={
                        loginErrorKind === "exited" ? "Got it" : "Try again"
                      }
                      onClick={() => {
                        setLoginError(null);
                        setLoginErrorKind("generic");
                      }}
                      btnClassName="w-100 py-2"
                      BorderColor="#8b313a"
                      ButtonInlineStyle={{
                        backgroundColor: "#8b313a",
                        color: "#ffffff",
                        borderRadius: 10,
                        minWidth: 180,
                        border: "none",
                      }}
                    />
                  </div>
                ),
              },
            ]}
          />
        </>
    </>
  );
};

const Style = styledComponents.div`
// height:100vh;

.carousel-caption{
  text-align:left;
}
.carousel-indicators{
display:none;
}


h6.background {
  position: relative;
  z-index: 1;
  
  &:before {
      border-top: 1px solid #6B6B6B;
      content:"";
      margin: 0 auto; /* this centers the line to the full width specified */
      position: absolute; /* positioning must be absolute here, and relative positioning must be applied to the parent */
      top: 50%; left: 0; right: 0; bottom: 0;
      width: 95%;
      z-index: -1;
  }

  span { 
      /* to hide the lines from behind the text, you have to set the background color the same as the container */ 
      background: #fff; 
      padding: 0 15px; 
  }
}
@media only screen and (max-width: 991px) {
  .carousel-inner{
    height:50vh;
  }
}
@media only screen and (max-width: 491px) {
  .carousel-inner{
    height:30vh;
  }
  p{
    font-size:14px;
  }
}
`;
